import type { BreadcrumbItem, BreadcrumbSibling } from '@/components/Breadcrumb/Breadcrumb';

import type { SettingsSection, SettingsSectionDef } from './settings-sections';

export const SETTINGS_ROOT = '/settings';

const TITLE = 'Settings';

/**
 * The Settings header's breadcrumb, built like Maintain's: the screen's own
 * name as the root crumb, the open section beside it, and a switcher on the
 * last crumb listing the sections you would actually move between.
 *
 * Sections live in `?tab=` rather than in the path, so this can't lean on the
 * shared path-driven `useBreadcrumb` — it reads the same `active` value the
 * sidebar highlights and writes through the same `onOpen`/`onClose` the list
 * uses, so the crumb, the rail and the pane can never disagree.
 */
export const useSettingsBreadcrumb = ({
  sections,
  active,
  /** A section is open only once `?tab=` names one — desktop still previews the first. */
  hasOpenSection,
  onOpen,
  onClose,
}: {
  sections: readonly SettingsSectionDef[];
  active: SettingsSection;
  hasOpenSection: boolean;
  onOpen: (value: SettingsSection) => void;
  onClose: () => void;
}): {
  items: BreadcrumbItem[];
  siblings: BreadcrumbSibling[];
  parent?: BreadcrumbSibling;
  backTo?: string;
} => {
  const root: BreadcrumbSibling = { label: TITLE, onClick: onClose };

  const siblings: BreadcrumbSibling[] = sections.map((sec) => ({
    label: sec.label,
    onClick: () => onOpen(sec.value),
    current: sec.value === active,
  }));

  if (!hasOpenSection) return { items: [{ label: TITLE }], siblings };

  const label = sections.find((sec) => sec.value === active)?.label ?? TITLE;

  return {
    items: [{ label: TITLE, onClick: onClose }, { label }],
    siblings,
    parent: root,
    backTo: SETTINGS_ROOT,
  };
};
